import React, {memo, useCallback, useState} from 'react';
import {useMediaQuery} from "@/app/utils/custom_function";
import {useMatchDetailPage} from "@/app/features/match_detail/slice";

import Breadcrumbs from "@/app/components/breadcrumbs";
import {TextBold14, TextBold16, TextBold20, TextMedium16, ThirtyText} from "@/app/components/texts";
import CircularProgressIndicator from "@/app/components/circular_progress_indicator";
import UserRowWidget from "@/app/components/user_row_widget";
import Chip from "@/app/components/chip/widget";
import MapWithRadiusAndPoint from "@/app/components/map_static_with_radius_and_point";
import CourtInfoWidget from "@/app/components/court_info_widget/widget";
import Button from "@/app/components/button";
import BottomSheet from "@/app/components/bottom_sheet/widget";
import ReviewForm from "@/app/components/review_form/widget";

import {t} from "i18next";
import {s} from "@/app/utils/s";
import styles from "./styles.module.scss";
import {OpenMatchStatusEnum} from "@/app/api/open_match/models";
import PeopleNearYou from "@/app/features/find_players/components/people_near_you/widget";
import classNames from "classnames";
import Space from "@/app/components/space";

const MatchDetailPage = () => {
  const isDesktop = useMediaQuery('(min-width:1024px)');
  const {
    state,
    addUser,
    removeUser,
    updateMatchStatus,
    leaveMatch,
    toggleShowReview,
    onSetReview,
    setRating,
    sendReview,
    userInMatch
  } = useMatchDetailPage();

  // State for bottom sheets
  const [showAddUser, setShowAddUser] = useState(false);
  const [showRemoveUser, setShowRemoveUser] = useState(false);

  // Toggle functions for bottom sheets
  const toggleShowAddUser = useCallback(() => setShowAddUser(!showAddUser), [showAddUser]);
  const toggleShowRemoveUser = useCallback(() => setShowRemoveUser(!showRemoveUser), [showRemoveUser]);

  // Dummy function for UserRowWidget onClick
  const onUserClick = useCallback(() => {
  }, []);

  // Functions for user actions
  const onUserSelect = useCallback((user: any) => {
    addUser(user.id);
    toggleShowAddUser();
  }, [addUser, toggleShowAddUser]);

  const onUserRemove = useCallback((user: any) => {
    removeUser(user.id);
    toggleShowRemoveUser();
  }, [removeUser, toggleShowRemoveUser]);

  // Render buttons based on user role and match status
  const renderButtons = useCallback(() => {

    const {match, isAdmin, currentUser} = state;

    if (!match) return null;

    const users = match.users?.filter((u) => u.id !== currentUser?.user.id);

    // Check if user has already left a review
    const hasLeftReview = match.reviews?.includes(currentUser?.user.id ?? 0);

    // Owner buttons
    if (isAdmin) {
      switch (match.status) {
        case OpenMatchStatusEnum.NOT_STARTED:
          return (
            <>
              {match.users && match.users.length < 3 && (
                <Button
                  text={t(s.addUser)}
                  onClick={toggleShowAddUser}
                  isLoading={state.hasLoadingAddUser}
                />
              )}
              {match.users && match.users.length > 1 && (
                <Button
                  text={t(s.removeUser)}
                  onClick={toggleShowRemoveUser}
                  isLoading={state.hasLoadingRemoveUser}
                />
              )}
              {match.users && match.users.length > 0 && users && users.length > 0 && (
                <Button
                  text={t(s.startMatch)}
                  onClick={() => updateMatchStatus(OpenMatchStatusEnum.STARTED)}
                  isLoading={state.hasLoadingUpdateStatus}
                />)}
              <Button
                text={t(s.cancelMatch)}
                onClick={() => updateMatchStatus(OpenMatchStatusEnum.CANCELED)}
                isLoading={state.hasLoadingUpdateStatusCancel}
                isRed
                isBlackLoading
                isOutline
              />
            </>
          );
        case OpenMatchStatusEnum.STARTED:
          return (
            <Button
              text={t(s.endMatch)}
              onClick={() => updateMatchStatus(OpenMatchStatusEnum.ARCHIVED)}
              isLoading={state.hasLoadingUpdateStatus}
            />
          );
        case OpenMatchStatusEnum.ARCHIVED:
          return !hasLeftReview ? (
            <Button
              text={t(s.leaveAReview)}
              onClick={toggleShowReview}
              isLoading={state.hasLoadingReview}
            />
          ) : null;
        default:
          return null;
      }
    }
    // Player buttons
    else {
      switch (match.status) {
        case OpenMatchStatusEnum.NOT_STARTED:
          return (
            <>
              {userInMatch(state.currentUser?.user?.id ?? 0) ? (
                <Button
                  text={t(s.leaveMatch)}
                  onClick={leaveMatch}
                  isLoading={state.hasLoadingLeaveMatch}
                  isRed
                  isBlackLoading
                  isOutline
                />
              ) : (
                <>
                  {state.currentUser?.user && (
                    <Button
                      text={t(s.joinMatch)}
                      onClick={() => addUser(state.currentUser?.user?.id ?? 0)}
                      isLoading={state.hasLoadingAddUser}
                    />
                  )}
                </>
              )}
            </>
          );
        case OpenMatchStatusEnum.ARCHIVED:
          return !hasLeftReview ? (
            <Button
              text={t(s.leaveAReview)}
              onClick={toggleShowReview}
              isLoading={state.hasLoadingReview}
            />
          ) : null;
        default:
          return null;
      }
    }
  }, [state, toggleShowAddUser, toggleShowRemoveUser, updateMatchStatus, toggleShowReview, leaveMatch]);

  if (state.hasLoading) {
    return (<div className={styles.matchDetail}><CircularProgressIndicator isGreen/></div>);
  }
  if (state.hasError || !state.match) {
    return (<div className={styles.matchDetail}><ThirtyText text={state.hasError || t(s.notFound)}/></div>);
  }

  const {match, location, reviews, users, owner, otherPlayers} = state;

  return (
    <div className={styles.matchDetail}>
      <Breadcrumbs items={state.items || []}/>

      {isDesktop ? (
        <div className={styles.desktopLayout}>
          {/* First Column - Match Information */}
          <div className={styles.column}>
            {/* Match Information */}
            <div className={styles.section}>
              <div className={styles.infoRow}>
                {match.eventDate && (
                  <div className={styles.dateInfo}>
                    <ThirtyText
                      text={new Date(match.eventDate).toLocaleDateString('en-US', {month: 'long', day: 'numeric'})}/>
                  </div>
                )}

                {match.skillLevel && (
                  <Chip text={match.skillLevel.toLowerCase() === 'beginner' ? t(s.beginner) :
                    match.skillLevel.toLowerCase() === 'intermediate' ? t(s.intermediate) :
                      match.skillLevel.toLowerCase() === 'advanced' ? t(s.advanced) : match.skillLevel}/>
                )}

                {match.timeRanges && (
                  <Chip text={t(match.timeRanges.toLowerCase() === 'morning' ? s.morning :
                    match.timeRanges.toLowerCase() === 'midday' ? s.midday :
                      match.timeRanges.toLowerCase() === 'evening' ? s.evening : match.timeRanges)}/>
                )}
              </div>
            </div>

            {/* You Play With Section */}
            <div className={styles.section}>
              <div className={styles.title}>
                <ThirtyText text={t(s.youPlayWith)}/>
              </div>
              <div className={styles.usersList}>
                {otherPlayers && otherPlayers.length > 0 ? (
                  otherPlayers.map(player => (
                    <UserRowWidget
                      key={player.id}
                      user={player}
                      onClick={onUserClick}
                      isDesktop={isDesktop}
                    />
                  ))
                ) : (
                  <ThirtyText text={t(s.noOtherPlayers)}/>
                )}
              </div>
            </div>

            {/* Owner Section */}
            <div className={styles.section}>
              <div className={styles.title}>
                <ThirtyText text={t(s.owner)}/>
              </div>
              <div className={styles.usersList}>
                {owner && (
                  <UserRowWidget
                    user={owner}
                    onClick={onUserClick}
                    isDesktop={isDesktop}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Second Column - Court and Reviews */}
          <div className={styles.column}>
            {/* Location Section */}
            <div className={styles.section}>
              <div className={styles.title}>
                <ThirtyText text={t(s.location)}/>
              </div>
              <div className={styles.location}>
                {!location && (
                  <div className={styles.notSelected}>
                    <TextMedium16 text={t(s.courtNotSelected)}/>
                  </div>
                )}
                {location && (
                  <CourtInfoWidget
                    data={location}
                    reviews={reviews}
                    goToCreateMatch={() => {
                    }}
                    courtOnMatch
                  />
                )}
              </div>
            </div>
          </div>

          {/* Third Column - Content and Buttons */}
          {/* Content area that stretches to full width */}

          {/* Buttons positioned at the bottom */}
          <div className={classNames(styles.column, styles.buttons)}>
            <div className={styles.columnContent}>
              <ThirtyText text={t(s.matchDetails)}/>
              <TextBold20 text={match?.commentMessage ?? ''}/>
            </div>
            {renderButtons()}
          </div>
        </div>
      ) : (
        <>
          {/* Mobile Layout */}
          {/* Match Information */}
          <div className={styles.section}>
            <div className={styles.infoRow}>
              {match.eventDate && (
                <div className={styles.dateInfo}>
                  <ThirtyText
                    text={new Date(match.eventDate).toLocaleDateString('en-US', {month: 'long', day: 'numeric'})}/>
                </div>
              )}

              {match.skillLevel && (
                <Chip text={match.skillLevel.toLowerCase() === 'beginner' ? t(s.beginner) :
                  match.skillLevel.toLowerCase() === 'intermediate' ? t(s.intermediate) :
                    match.skillLevel.toLowerCase() === 'advanced' ? t(s.advanced) : match.skillLevel}/>
              )}

              {match.timeRanges && (
                <Chip text={t(match.timeRanges.toLowerCase() === 'morning' ? s.morning :
                  match.timeRanges.toLowerCase() === 'midday' ? s.midday :
                    match.timeRanges.toLowerCase() === 'evening' ? s.evening : match.timeRanges)}/>
              )}
            </div>
          </div>

          {/* You Play With Section */}
          <div className={styles.section}>
            <div className={styles.title}>
              <ThirtyText text={t(s.youPlayWith)}/>
            </div>
            <div className={styles.usersList}>
              {otherPlayers && otherPlayers.length > 0 ? (
                otherPlayers.map(player => (
                  <UserRowWidget
                    key={player.id}
                    user={player}
                    onClick={onUserClick}
                    isDesktop={isDesktop}
                  />
                ))
              ) : (
                <ThirtyText text={t(s.noOtherPlayers)}/>
              )}
            </div>
          </div>

          {/* Owner Section */}
          <div className={styles.section}>
            <div className={styles.title}>
              <ThirtyText text={t(s.owner)}/>
            </div>
            <div className={styles.usersList}>
              {owner && (
                <UserRowWidget
                  user={owner}
                  onClick={onUserClick}
                  isDesktop={isDesktop}
                />
              )}
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.mainInfo}>
              <ThirtyText text={t(s.matchDetails)}/>
              <TextBold14 text={match?.commentMessage ?? ''} className={styles.address}/>
            </div>
          </div>

          {/* Location Section */}
          <div className={styles.section}>
            <div className={styles.title}>
              <ThirtyText text={t(s.location)}/>
            </div>
            <div className={styles.location}>
              {!location && (
                <div className={styles.notSelected}>
                  <TextMedium16 text={t(s.courtNotSelected)}/>
                </div>
              )}
              {location && (
                <>
                  <div className={styles.mainInfo}>
                    <ThirtyText text={location.name}/>
                    <TextBold14 text={location.address} className={styles.address}/>
                  </div>
                  <div className={styles.chips}>
                    {location?.type !== undefined && location?.type.length > 0 &&
                      location?.type.map((type, index) => (<Chip key={index} text={type}/>))}
                    {location?.bookingRequired && <Chip text={t(s.bookingRequired)}/>}
                    {location?.lockerRooms && <Chip text={t(s.lockerRooms)}/>}
                    {location?.artificialLight && <Chip text={t(s.artificialLight)}/>}
                    {location?.parkingAvailability && <Chip text={t(s.parkingAvailability)}/>}
                  </div>
                  <div className={styles.map}>
                    <MapWithRadiusAndPoint
                      radiusKm={2}
                      getCoords={() => {
                      }}
                      coords={{latitude: location.latitude, longitude: location.longitude}}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Buttons Section for Mobile */}
          <div className={styles.buttons}>
            {renderButtons()}
          </div>
        </>
      )}

      {/* Add User Bottom Sheet */}
      <BottomSheet open={showAddUser} onClose={toggleShowAddUser} withPadding withCloseButton>
        <PeopleNearYou inChat onClick={onUserSelect} withoutButtons/>
      </BottomSheet>

      {/* Remove User Bottom Sheet */}
      <BottomSheet open={showRemoveUser} onClose={toggleShowRemoveUser} withPadding withCloseButton>
        <div className={styles.removeUsers}>
          <Space.H24/>
          <ThirtyText text={t(s.removeUserFromMatch)}/>
          <div className={classNames(styles.usersList, styles.usersListRemove, {[styles.onlyOne]: state?.users?.length === 1}, {[styles.onlyTwo]: state?.users?.length === 2})}>
            {state.users && state.users.length > 0 && state.users.map(player => (
              <UserRowWidget
                key={player.id}
                user={player}
                onClick={onUserRemove}
                isDesktop={isDesktop}
              />
            ))}
          </div>
        </div>
      </BottomSheet>

      {/* Review Form */}
      <ReviewForm
        isOpen={state.showReview ?? false}
        onClose={toggleShowReview}
        onSetReview={onSetReview}
        setRating={setRating}
        sendReview={sendReview}
        review={state.review}
        rating={state.rating}
        isLoading={state.hasLoadingSendReview}
        isDesktop={isDesktop}
      />
    </div>
  );
};

export default memo(MatchDetailPage);